import React from 'react';
import { ContentBlock, ContentBlockProps } from '@jbhi-fi/components/src/ContentBlock';
import { PresentationProductCarousel, ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { ContentBanner } from '@jbhi-fi/components/src/ContentBanner/ContentBanner';
import { PresentationVideoCardCarousel } from '@jbhi-fi/components/src/Card/VideoCardCarousel/VideoCardCarousel';
import { mergeResponsiveValues, ResponsiveValue } from '@jbhi-fi/utils/src/responsiveValue/responsiveValue';
import { CardGrid } from '@jbhi-fi/components/src/Card';
import { CardGridVariant } from '@jbhi-fi/components/src/Card/CardGrid/CardGrid';
import { HeroBlock } from '@jbhi-fi/components/src/HeroBlock';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { useResponsiveValue } from '@jbhi-fi/components/src/hooks/useResponsiveValue';
import { styles } from './Section.css';
import { spacing } from '@jbhi-fi/lanyard-ui/themes';
import { ContentTheme } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { ListingBlock } from '@jbhi-fi/components/src/ListingBlock';
import { TabsBlock } from '@jbhi-fi/components/src/TabsBlock/TabsBlock';
import { MultiColumnBlock } from '@jbhi-fi/components/src/MultiColumnBlock/MultiColumnBlock';
import { SideBySide } from '@jbhi-fi/components/src/SideBySide/SideBySide';
import { overrideElementTheme } from './overrideElementTheme';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { Callout } from '@jbhi-fi/components/src/Callout/Callout';
import { ContentAlert } from '@jbhi-fi/components/src/ContentAlert/ContentAlert';
import { CarouselLeadImage } from '../CarouselLeadImage/CarouselLeadImage';
import { ProductGrid } from '../ProductGrid/ProductGrid';
import { CardGroupRenderer } from '../Contentful/CardGroup/CardGroupRenderer';
import { ContentfulCardGrid } from '../Contentful/CardGrid/ContentfulCardGrid';
import { MiniCardGroupRenderer } from '../Contentful/MiniCardGroup/MiniCardGroupRenderer';
import { HeadingBlock } from '../HeadingBlock/HeadingBlock';
import { determineContactBlock } from '../ContactBlock/determineContactBlock';

const DEFAULT_SECTION_LAYOUT = 'Full width';

export enum ElementType {
  contentfulCardGrid = 'contentfulCardGrid',
  callout = 'callout',
  contentAlert = 'contentAlert',
  cardGrid = 'cardGrid',
  cardGroup = 'cardGroup',
  miniCardGroup = 'miniCardGroup',
  cardGridWithPrimary = 'cardGridWithPrimary',
  heroBlock = 'heroBlock',
  productCarousel = 'productCarousel',
  carouselLeadImage = 'carouselLeadImage',
  countPresentationProductCarousel = 'countPresentationProductCarousel',
  presentationProductCarousel = 'presentationProductCarousel',
  presentationVideoCarousel = 'presentationVideoCarousel',
  cardCarousel = 'cardCarousel',
  contentBanner = 'contentBanner',
  listingBlock = 'listingBlock',
  productBlock = 'productBlock',
  articleBlock = 'articleBlock',
  multiColumnBlock = 'multiColumnBlock',
  sideBySide = 'sideBySide',
  promoCardCarousel = 'promoCardCarousel',
  productGrid = 'productGrid',
  contactBlock = 'contactBlock',
}
const ElementsWithTheme = [
  ElementType.contentBanner,
  ElementType.listingBlock,
  ElementType.productBlock,
  ElementType.multiColumnBlock,
  ElementType.productGrid,
  ElementType.carouselLeadImage,
];
export type SectionElementProps<T> = {
  type: ElementType;
  props: T;
};

export type SectionProps = {
  title?: string;
  subText?: string;
  href?: string;
  headingStyle?: 'Left' | 'Centred' | 'Banner' | 'Hidden';
  shouldOpenInNewWindow?: boolean;
  spaceBetweenElements?: Partial<ResponsiveValue<number>> | number;
  titlePadding?: Partial<ResponsiveValue<number>> | number;
  titleColor?: 'white' | 'black';
  titleVariant?: HeadingDividerVariant;
  contentBlockProps?: Omit<ContentBlockProps, 'children'>;
  leftColumnElements: Array<SectionElementProps<any>>;
  rightColumnElements?: Array<SectionElementProps<any>>;
  layout?: 'Full width' | 'Half width' | '2/3' | '1/3';
  contentPalette?: ContentTheme['palette'];
  isShowcase?: boolean;
};

const elementMap: Record<
  ElementType,
  { getComponent: () => React.FunctionComponent<any>; defaultProps?: { [key: string]: any } }
> = {
  [ElementType.contentfulCardGrid]: { getComponent: () => ContentfulCardGrid },
  [ElementType.callout]: { getComponent: () => Callout },
  [ElementType.contentAlert]: { getComponent: () => ContentAlert },
  [ElementType.cardGrid]: {
    getComponent: () => CardGrid,
    defaultProps: { variant: CardGridVariant.default, shouldShowMobileCarousel: true },
  },
  [ElementType.cardGroup]: { getComponent: () => CardGroupRenderer },
  [ElementType.miniCardGroup]: { getComponent: () => MiniCardGroupRenderer },
  [ElementType.cardGridWithPrimary]: {
    getComponent: () => CardGrid,
    defaultProps: { variant: CardGridVariant.withPrimary },
  },
  [ElementType.heroBlock]: { getComponent: () => HeroBlock },
  [ElementType.productCarousel]: { getComponent: () => ProductCarousel },
  [ElementType.carouselLeadImage]: { getComponent: () => CarouselLeadImage },
  [ElementType.countPresentationProductCarousel]: { getComponent: () => PresentationProductCarousel },
  [ElementType.presentationProductCarousel]: { getComponent: () => PresentationProductCarousel },
  [ElementType.presentationVideoCarousel]: { getComponent: () => PresentationVideoCardCarousel },
  [ElementType.cardCarousel]: {
    getComponent: () => Carousel,
    defaultProps: {
      fullHeightItems: true,
      showProgressBar: true,
      showArrows: true,
      spaceBetween: 10,
    },
  },
  [ElementType.contentBanner]: { getComponent: () => ContentBanner },
  [ElementType.listingBlock]: { getComponent: () => ListingBlock },
  [ElementType.productBlock]: { getComponent: () => TabsBlock },
  [ElementType.articleBlock]: { getComponent: () => TabsBlock },
  [ElementType.multiColumnBlock]: { getComponent: () => MultiColumnBlock },
  [ElementType.sideBySide]: { getComponent: () => SideBySide },
  [ElementType.promoCardCarousel]: { getComponent: () => ListingBlock },
  [ElementType.productGrid]: { getComponent: () => ProductGrid },
  [ElementType.contactBlock]: { getComponent: () => determineContactBlock() },
};
const DEFAULT_CONTENT_BLOCK_PROPS: Partial<ContentBlockProps> = {
  paddingTop: { mobile: 4, tablet: 7.5 },
  paddingBottom: { mobile: 4, tablet: 7.5 },
};

const getRenderedElement = (
  element: SectionElementProps<any>,
  key: number,
  contentPalette?: ContentTheme['palette'],
  layout = DEFAULT_SECTION_LAYOUT
) => {
  const { getComponent, defaultProps } = elementMap[element.type];
  let updatedProps = element.props;
  if (contentPalette && ElementsWithTheme.includes(element.type)) {
    updatedProps = overrideElementTheme(element.type, element.props, contentPalette);
  }
  const Component = getComponent();
  return (
    <Component
      key={key}
      {...defaultProps}
      {...updatedProps}
      enableContainerBreakpoints={layout !== DEFAULT_SECTION_LAYOUT}
    />
  );
};

export const Section = ({
  title,
  href,
  shouldOpenInNewWindow,
  spaceBetweenElements,
  titlePadding,
  titleVariant,
  titleColor,
  contentBlockProps = {},
  leftColumnElements,
  contentPalette,
  rightColumnElements = [],
  layout = DEFAULT_SECTION_LAYOUT,
  headingStyle,
  subText,
  isShowcase = false,
}: SectionProps) => {
  const titlePaddingResponsive = mergeResponsiveValues<number>({ desktopLg: 9, desktop: 9, mobile: 5 }, titlePadding);
  const titlePaddingFinal = useResponsiveValue<number>({
    xs: titlePaddingResponsive.mobile,
    md: titlePaddingResponsive.desktop,
    lg: titlePaddingResponsive.desktopLg,
  });
  const isDesktop = useBreakpoint('md', 'up');

  const spaceBetweenElementsResponsive = mergeResponsiveValues<number>(
    { desktopLg: 10, desktop: 10, mobile: 7.5 },
    spaceBetweenElements
  );
  const spaceBetweenElementsFinal = useResponsiveValue<number>({
    xs: spaceBetweenElementsResponsive.mobile,
    md: spaceBetweenElementsResponsive.desktop,
    lg: spaceBetweenElementsResponsive.desktopLg,
  });

  if (!leftColumnElements?.length) return null;

  const isBanner = headingStyle === 'Banner';
  const marginTopOverride = isBanner ? (isDesktop ? 20 : 7.5) : undefined;
  const headingVariant = isShowcase ? HeadingDividerVariant.large : titleVariant;

  return (
    <ContentBlock
      {...DEFAULT_CONTENT_BLOCK_PROPS}
      {...contentBlockProps}
      maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
      style={{
        backgroundColor: contentPalette?.background,
        marginTop: marginTopOverride ? spacing(marginTopOverride) : undefined,
      }}
    >
      {title ? (
        <>
          <HeadingBlock
            heading={title}
            subText={subText}
            href={href}
            shouldOpenInNewWindow={shouldOpenInNewWindow}
            contentPalette={contentPalette}
            variant={headingVariant}
            style={{
              marginBottom: spacing(titlePaddingFinal),
            }}
            headingStyle={headingStyle ?? 'Left'}
          />
        </>
      ) : null}
      <div className={styles.elementsContainer({ layout: layout })} style={{ gap: spacing(spaceBetweenElementsFinal) }}>
        {[leftColumnElements, rightColumnElements]
          .filter(col => col && col.length > 0)
          .map((column, colIdx) => (
            <div
              key={colIdx}
              className={styles.sectionColumn({ hasContainerQuery: layout !== DEFAULT_SECTION_LAYOUT })}
              style={{ gap: spacing(spaceBetweenElementsFinal) }}
            >
              {column.map((element, i) => getRenderedElement(element, i, contentPalette, layout))}
            </div>
          ))}
      </div>
    </ContentBlock>
  );
};
